import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { announcementService, academicService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Field, Select, Textarea, EmptyState, Spinner, Pagination } from '../../components/ui';

const AUDIENCE_TONE = { all: 'blue', students: 'green', teachers: 'purple', parents: 'yellow' };
const EMPTY_FORM = { title: '', body: '', audience: 'all', grade: '', is_active: true };
const PAGE_SIZE = 20;

function AdminAnnouncements() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      announcementService.getAnnouncements({ page, page_size: PAGE_SIZE }),
      academicService.getGrades({ page_size: 100 }),
    ])
      .then(([annRes, gradesRes]) => {
        const annData = annRes.data;
        setAnnouncements(Array.isArray(annData) ? annData : annData.results || []);
        setCount(Array.isArray(annData) ? annData.length : annData.count || 0);
        const gradesData = gradesRes.data;
        setGrades(Array.isArray(gradesData) ? gradesData : gradesData.results || []);
      })
      .catch(() => addNotification(t('Failed to load announcements'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAll, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, body: a.body, audience: a.audience, grade: a.grade || '', is_active: a.is_active });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      addNotification(t('Title and message are required'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        audience: form.audience,
        grade: form.audience === 'students' && form.grade ? form.grade : null,
        is_active: form.is_active,
      };
      if (editing) {
        const res = await announcementService.updateAnnouncement(editing.id, payload);
        setAnnouncements((prev) => prev.map((a) => (a.id === editing.id ? res.data : a)));
      } else {
        const res = await announcementService.createAnnouncement(payload);
        setAnnouncements((prev) => [res.data, ...prev]);
        setCount((current) => current + 1);
      }
      addNotification(t('Announcement saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save announcement'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!(await confirm(t('Delete "{{title}}"?', { title: a.title })))) return;
    try {
      await announcementService.deleteAnnouncement(a.id);
      setAnnouncements((prev) => prev.filter((x) => x.id !== a.id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('Announcement deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete announcement'), 'error');
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Announcements')} subtitle={t('Broadcast a notice to everyone, a role, or one grade level')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openCreate}>+ {t('New Announcement')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : announcements.length === 0 ? (
            <Card><EmptyState title={t('No announcements yet')} action={<Button onClick={openCreate}>+ {t('New Announcement')}</Button>} /></Card>
          ) : (
            <div className="space-y-3">
              {announcements.map((a) => (
                <Card key={a.id} padded={false} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-gray-900">{a.title}</h3>
                        <Badge tone={AUDIENCE_TONE[a.audience]}>{t(a.audience)}</Badge>
                        {a.grade_detail && <Badge tone="gray">{a.grade_detail.name}</Badge>}
                        {!a.is_active && <Badge tone="gray">{t('Inactive')}</Badge>}
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{a.body}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {t('By {{name}}', { name: a.created_by ? `${a.created_by.first_name} ${a.created_by.last_name}` : t('system') })}
                        {' · '}{new Date(a.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>{t('Edit')}</Button>
                      <Button size="sm" variant="danger" onClick={() => remove(a)}>{t('Delete')}</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Announcement') : t('New Announcement')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Title')} required>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('e.g. Midterm exam schedule')} />
          </Field>
          <Field label={t('Message')} required>
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} />
          </Field>
          <Field label={t('Audience')}>
            <Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value, grade: '' })}>
              <option value="all">{t('Everyone')}</option>
              <option value="students">{t('Students')}</option>
              <option value="teachers">{t('Teachers')}</option>
              <option value="parents">{t('Parents')}</option>
            </Select>
          </Field>
          {form.audience === 'students' && (
            <Field label={t('Grade Level (optional)')} hint={t('Leave blank to reach students in every grade')}>
              <Select value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}>
                <option value="">{t('All grades')}</option>
                {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </Select>
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active')}
          </label>
        </div>
      </Modal>
    </div>
  );
}

export default AdminAnnouncements;
